// File: pages/Reminder.js
import React, { useState, useEffect } from "react";
import { useTheme } from "../components/ThemeContext";
import axios from "axios";
import moment from "moment";
import { Button, Container, Row, Col, Spinner } from "react-bootstrap";
import NavMenu from "../components/NavMenu";
import DepartmentCard from "../components/DepartmentCard";
import StaffTable from "../components/StaffTable";
import AppraisalDropdown from "../components/AppraisalDropdown";
import SendModal from "../components/SendModal";
import StaffModal from "../components/StaffModal";
import { BsBuildingFillCheck } from "react-icons/bs";
import { FaUsers } from "react-icons/fa6";
import { FaEnvelope } from "react-icons/fa";

const Reminder = () => {
  const [viewMode, setViewMode] = useState("department");
  const [departments, setDepartments] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roles, setRoles] = useState([]);
  const [appraisal, setAppraisal] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedSupervisorId, setSelectedSupervisorId] = useState(null);
  const [selectedAppraisal, setSelectedAppraisal] = useState(null);
  const [showStaffModal, setShowStaffModal] = useState(false);
  const [selectedEmp, setSelectedEmp] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState({});
  const [selectedValue, setSelectedValue] = useState("Select Appraisal");
  const [staffDepartment, setStaffDepartment] = useState([]);
  const [sendLoading, setSendLoading] = useState(false);
  const { isDark } = useTheme();

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found in localStorage");
      }
      try {
        const [deptRes, staffRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API}/getDepartments`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
          axios.get(`${process.env.REACT_APP_API}/getStaff`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }),
        ]);
        setDepartments(deptRes.data.data);
        setStaff(staffRes.data.data);
      } catch (err) {
        setError("Failed to fetch departments or staff.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchAppraisal = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found in localStorage");
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}/getAppraisal`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data.success) {
          setAppraisal(response.data.data);
        }
      } catch (err) {
        console.error("Failed to fetch appraisal:", err);
      }
    };

    fetchAppraisal();
  }, []);

  useEffect(() => {
    if (!selectedEmp?.id) return;
    const fetchRoles = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found in localStorage");
      }
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API}/getStaffIndividual`,
          {
            params: { staff_id: selectedEmp.id },
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response.data.success) setRoles(response.data.data);
      } catch (err) {
        console.error("Failed to fetch staff roles:", err);
      }
    };
    fetchRoles();
  }, [selectedEmp?.id]);

  const formatDate = (dateStr) => moment(dateStr).format("DD-MM-YYYY");

  const handleSelectAppraisal = (eventKey) => {
    const selected = appraisal.find(
      (a) => String(a.appraisal_id) === String(eventKey)
    );
    setSelectedAppraisal(selected);
    if (selected) {
      const validStart =
        selected.start_date && selected.start_date !== "0000-00-00";
      const validDue = selected.due_date && selected.due_date !== "0000-00-00";

      const displayText =
        validStart && validDue
          ? `${selected.appraisal_name} (${formatDate(
              selected.start_date
            )} - ${formatDate(selected.due_date)})`
          : selected.appraisal_name;

      setSelectedValue(displayText);
    } else {
      setSelectedValue("Select Appraisal");
    }
  };

  const groupedStaff = staff.reduce((acc, emp) => {
    if (!acc[emp.department]) acc[emp.department] = [];
    acc[emp.department].push(emp);
    return acc;
  }, {});

  const fetchStaffDepartment = async (supervisorId) => {
    const token = localStorage.getItem("authToken");
    if (!token) {
      throw new Error("No auth token found in localStorage");
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API}/getStaffDepartment`,
        {
          params: { supervisor_id: supervisorId },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.data.success) setStaffDepartment(response.data.data);
    } catch (error) {
      console.error("API error:", error);
    }
  };

  const handleShowModal = async (departmentName, supervisorId) => {
    setSelectedDepartment(departmentName);
    setSelectedSupervisorId(supervisorId);
    await fetchStaffDepartment(supervisorId);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedDepartment(null);
    setSelectedSupervisorId(null);
  };

  const handleShowStaffModal = (emp) => {
    setSelectedEmp(emp);
    setSelectedRoles({});
    setShowStaffModal(true);
  };

  const handleCloseStaffModal = () => {
    setShowStaffModal(false);
    setSelectedEmp(null);
  };

  const handleCheckboxChange = (uniqueKey) => {
    setSelectedRoles((prev) => ({
      ...prev,
      [uniqueKey]: !prev[uniqueKey],
    }));
  };

  const handleCheckAll = () => {
    const allSelected = {};
    staffDepartment.forEach((staff) => {
      const key = `${staff.staff_id}_${staff.role_id}`;
      allSelected[key] = true;
    });
    setSelectedRoles(allSelected);
  };

  const handleSend = async () => {
    setSendLoading(true);
    try {
      const selectedStaff = staffDepartment.filter(
        (staff) => selectedRoles[`${staff.staff_id}_${staff.role_id}`]
      );
      if (selectedStaff.length === 0 || !selectedAppraisal?.appraisal_id) {
        alert("Please select at least one staff and an appraisal.");
        return;
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found in localStorage");
      }

      const sendData = selectedStaff.map((staff) => ({
        appraisal_id: selectedAppraisal.appraisal_id,
        department: selectedDepartment,
        role_id: staff.role_id,
        staff_id: staff.staff_id,
        supervisor_id: selectedSupervisorId || 1,
        status: "pending",
        created_at: moment().format("YYYY-MM-DD"),
      }));

      const response = await axios.post(
        `${process.env.REACT_APP_API}/sendAppraisal`,
        { data: sendData },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        const user = JSON.parse(localStorage.getItem("user"));
        const created_by = user?.id;

        const inserted = response.data.inserted || [];

        for (const item of inserted) {
          await axios.post(
            `${process.env.REACT_APP_API}/saveHistory`,
            {
              send_id: item.send_id,
              description: "PA Assessment sent",
              created_by,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
        }

        // await axios.post(`${process.env.REACT_APP_API}/sendEmail`, {
        //   staff_ids: selectedStaff.map((staff) => staff.staff_id),
        // });

        alert("Appraisals sent, emails delivered, and history saved!");
        handleCloseModal();
      } else {
        alert("Failed to send appraisal form.");
      }
    } catch (err) {
      console.error("Send Error:", err);
      alert("Failed to send appraisal.");
    } finally {
      setSendLoading(false);
    }
  };

  const handleStaffSend = async () => {
    setSendLoading(true);
    try {
      const selectedRoleIds = Object.entries(selectedRoles)
        .filter(([_, isSelected]) => isSelected)
        .map(([roleId]) => roleId);

      if (
        !selectedEmp?.id ||
        selectedRoleIds.length === 0 ||
        !selectedAppraisal?.appraisal_id
      ) {
        alert("Missing required information.");
        return;
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found in localStorage");
      }

      const sendData = selectedRoleIds.map((role_id) => {
        return {
          appraisal_id: selectedAppraisal.appraisal_id,
          department: selectedEmp.department,
          role_id,
          staff_id: selectedEmp.id,
          supervisor_id: selectedEmp.supervisor || selectedSupervisorId || 1,
          status: "pending",
          created_at: moment().format("YYYY-MM-DD"),
        };
      });

      // Send individual appraisal
      const response = await axios.post(
        `${process.env.REACT_APP_API}/sendAppraisalIndividual`,
        sendData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        const inserted = response.data.inserted || [];
        const user = JSON.parse(localStorage.getItem("user"));
        const created_by = user?.id;

        // Save each to history
        for (const item of inserted) {
          await axios.post(
            `${process.env.REACT_APP_API}/saveHistory`,
            {
              send_id: item.send_id,
              description: "HR - PA Assessment sent",
              created_by,
            },
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
        }

        // await axios.post(`${process.env.REACT_APP_API}/sendEmail`, {
        //   staff_ids: [selectedEmp.id], // or pass multiple if needed
        // });

        alert("Appraisal form sent, email sent, and history saved!");
        handleCloseStaffModal();
      } else {
        alert("Failed to send appraisal form.");
      }
    } catch (error) {
      console.error("Send Error:", error);
      alert("An error occurred while sending the appraisal.");
    } finally {
      setSendLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!selectedAppraisal?.appraisal_id) {
      alert("Please select an appraisal before sending emails.");
      return;
    }

    const confirmSend = window.confirm(
      `Are you sure you want to send the appraisal email reminders to all staff under the selected appraisal?\n\nAppraisal: ${selectedAppraisal.appraisal_name}`
    );

    if (!confirmSend) {
      return; // user cancelled
    }

    try {
      setSendLoading(true);

      let staffIdsToSend = [];

      if (viewMode === "department" || viewMode === "staff") {
        staffIdsToSend = staff.map((emp) => emp.id);
      }

      if (staffIdsToSend.length === 0) {
        alert("No staff available to send emails.");
        return;
      }

      const token = localStorage.getItem("authToken");
      if (!token) {
        throw new Error("No auth token found in localStorage");
      }

      const user = JSON.parse(localStorage.getItem("user"));
      const user_id = user?.id;

      if (!user_id) {
        alert("No user ID found. Please re-login.");
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API}/sendEmailtoAll`,
        {
          staff_ids: staffIdsToSend,
          appraisal_id: selectedAppraisal.appraisal_id,
          user_id,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        alert("All appraisal records and emails processed successfully");
      } else {
        alert(response.data.message || "Failed to send emails.");
      }
    } catch (error) {
      console.error("Error sending emails:", error);
      alert("An error occurred while sending emails.");
    } finally {
      setSendLoading(false);
    }
  };

  const user = JSON.parse(localStorage.getItem("user"));
  const userDescription = user?.description || "";

  if (userDescription !== "HR" && userDescription !== "MIS") {
    return (
      <div
        className="d-flex flex-column justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <h1>Unauthorized</h1>
        <p>You do not have permission to view this page.</p>
      </div>
    );
  }

  return (
    <>
      <NavMenu />
      <Container
        fluid
        className="min-vh-100 p-4"
        style={{ paddingBottom: "80px" }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="header-title mb-0 fw-bold">Form Distribution</h5>
          <div className="d-flex align-items-center gap-2">
            <AppraisalDropdown
              appraisal={appraisal}
              selectedValue={selectedValue}
              formatDate={formatDate}
              onSelect={handleSelectAppraisal}
            />

            <Button
              size="sm"
              disabled={sendLoading}
              style={{
                backgroundColor: "#2c3e50",
                border: "1px solid #2c3e50",
                color: "white",
              }}
              onClick={handleSendEmail}
            >
              {sendLoading ? (
                <Spinner size="sm" animation="border" />
              ) : (
                <>
                  <FaEnvelope /> &nbsp; Send Email to All
                </>
              )}
            </Button>

            <Button
              size="sm"
              style={{
                backgroundColor:
                  viewMode === "department"
                    ? "#B31E8D"
                    : isDark
                    ? "#2c3e50"
                    : "white",
                border:
                  viewMode === "department"
                    ? "1px solid #B31E8D"
                    : isDark
                    ? "1px solid #ffffff"
                    : "1px solid #B31E8D",
                color:
                  viewMode === "department"
                    ? "white"
                    : isDark
                    ? "#ffffff"
                    : "#B31E8D",
              }}
              onClick={() => setViewMode("department")}
            >
              <BsBuildingFillCheck /> &nbsp; Departments
            </Button>
            <Button
              size="sm"
              style={{
                backgroundColor:
                  viewMode === "staff"
                    ? "#B31E8D"
                    : isDark
                    ? "#2c3e50"
                    : "white",
                border:
                  viewMode === "staff"
                    ? "1px solid #B31E8D"
                    : isDark
                    ? "1px solid #ffffff"
                    : "1px solid #B31E8D",
                color:
                  viewMode === "staff"
                    ? "white"
                    : isDark
                    ? "#ffffff"
                    : "#B31E8D",
              }}
              onClick={() => setViewMode("staff")}
            >
              <FaUsers /> &nbsp; All Staff
            </Button>
          </div>
        </div>

        {viewMode === "department" ? (
          loading ? (
            <div className="text-center mt-5">
              <Spinner animation="border" variant="secondary" />
              <p>Loading departments...</p>
            </div>
          ) : error ? (
            <div className="text-danger text-center">{error}</div>
          ) : (
            <Row xs={1} sm={2} md={3} lg={5} className="g-4">
              {departments.map((dept, index) => (
                <Col key={index}>
                  <DepartmentCard dept={dept} onSend={handleShowModal} />
                </Col>
              ))}
            </Row>
          )
        ) : (
          <StaffTable
            groupedStaff={groupedStaff}
            onSend={handleShowStaffModal}
          />
        )}

        <SendModal
          show={showModal}
          onClose={handleCloseModal}
          staffDepartment={staffDepartment}
          selectedRoles={selectedRoles}
          onCheck={handleCheckboxChange}
          onCheckAll={handleCheckAll}
          onSend={handleSend}
          loading={sendLoading}
        />

        <StaffModal
          show={showStaffModal}
          onClose={handleCloseStaffModal}
          roles={roles}
          selectedRoles={selectedRoles}
          onCheck={handleCheckboxChange}
          onSend={handleStaffSend}
          selectedEmp={selectedEmp}
          loading={sendLoading}
        />
      </Container>
    </>
  );
};

export default Reminder;
